import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { messageAPI, userAPI, IMG_URL } from '../utils/api';
import { Card, List, Avatar, Space, Typography, Spin, Empty, Button, Input, message, Tabs, Pagination } from 'antd';
import { UserOutlined, SendOutlined, DeleteOutlined } from '@ant-design/icons';
import useDocumentTitle from '../hooks/useDocumentTitle';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const Message = () => {
  const { username } = useParams();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState('received');
  const [messages, setMessages] = useState([]);
  const [content, setContent] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);

  // 设置网页标题
  useDocumentTitle('留言板', {
    prefix: '',
    suffix: ' - rtycn'
  });

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  // 获取用户名
  const getUsernameByParam = () => {
    return username;
  };

  useEffect(() => {
    if (username) {
      fetchMessages();
    }
  }, [username, activeTab, page]);

  const fetchMessages = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const targetUsername = getUsernameByParam();

      let response;
      if (activeTab === 'received') {
        response = await messageAPI.getReceivedMessages(targetUsername, page, pageSize);
      } else {
        response = await messageAPI.getSentMessages(targetUsername, page, pageSize);
      }

      if (response.success) {
        setMessages(response.data.list || []);
        setTotal(response.data.total || 0);
      }
    } catch (error) {
      console.error('获取留言失败:', error);
      message.error('获取留言失败，请刷新页面重试');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!content.trim()) {
      message.warning('请输入留言内容');
      return;
    }

    if (!currentUser) {
      message.warning('请先登录');
      return;
    }

    try {
      setSending(true);
      const receiverUsername = getUsernameByParam();

      await messageAPI.sendMessage({
        senderId: currentUser.username,
        receiverId: receiverUsername,
        content: content.trim()
      });

      message.success('留言发送成功');
      setContent('');
      if (activeTab === 'sent') {
        fetchMessages();
      }
    } catch (error) {
      console.error('留言发送失败:', error);
      message.error('留言发送失败，请稍后重试');
    } finally {
      setSending(false);
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      await messageAPI.markAsRead(messageId);
      setMessages(messages.map(msg =>
        msg.id === messageId ? { ...msg, status: 1 } : msg
      ));
      message.success('留言标记已读成功');
    } catch (error) {
      console.error('标记留言失败:', error);
      message.error('标记留言失败，请稍后重试');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await messageAPI.deleteMessage(messageId);
      setMessages(messages.filter(msg => msg.id !== messageId));
      setTotal(total - 1);
      message.success('留言删除成功');
    } catch (error) {
      console.error('删除留言失败:', error);
      message.error('删除留言失败，请稍后重试');
    }
  };

  const getUserInfo = async (userId) => {
    try {
      const response = await userAPI.getUser(userId);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const MessageItem = ({ msg, isReceived }) => {
    const [userInfo, setUserInfo] = useState(null);

    useEffect(() => {
      const fetchUserInfo = async () => {
        const targetUserId = isReceived ? msg.senderId : msg.receiverId;
        const info = await getUserInfo(targetUserId);
        setUserInfo(info);
      };
      fetchUserInfo();
    }, [msg, isReceived]);

    const canDelete = isReceived ? isOwner : msg.senderId === currentUser?.username;

    return (
      <List.Item
        actions={
          canDelete
            ? [
                isReceived && isOwner && msg.status === 0 && (
                  <Button type="link" size="small" onClick={() => handleMarkAsRead(msg.id)}>
                    标记已读
                  </Button>
                ),
                <Button danger type="link" size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(msg.id)}>
                  删除
                </Button>
              ]
            : []
        }
      >
        <List.Item.Meta
          avatar={
            <Avatar
              size={48}
              src={userInfo?.avatar ? `${IMG_URL}${userInfo.avatar}` : undefined}
              icon={<UserOutlined />}
            />
          }
          title={
            <Space>
              <Text strong>{userInfo?.username || '未知用户'}</Text>
              {msg.status === 0 && isReceived && <Text type="danger" style={{ fontSize: 12 }}>未读</Text>}
              <Text type="secondary" style={{ fontSize: 12 }}>
                {new Date(msg.createTime).toLocaleString('zh-CN')}
              </Text>
            </Space>
          }
          description={
            <div>
              {isReceived && <Text type="secondary">给我留言：</Text>}
              <Paragraph style={{ marginBottom: 0, marginTop: 8 }}>{msg.content}</Paragraph>
            </div>
          }
        />
      </List.Item>
    );
  };

  if (!currentUser) {
    return (
      <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', padding: 24 }}>
        <Card>
          <Empty description="请先登录查看留言" />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', padding: 24 }}>
      <Card
        title="留言板"
        tabList={[
          { key: 'received', tab: '收到的留言' },
          { key: 'sent', tab: '发送的留言' }
        ]}
        activeTabKey={activeTab}
        onTabChange={setActiveTab}
      >
        <div style={{ marginBottom: 24 }}>
          <Space.Compact style={{ width: '100%', display: 'flex' }}>
            <TextArea
              rows={4}
              placeholder="写留言..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={!currentUser}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={sending}
              onClick={handleSendMessage}
              disabled={!currentUser}
              style={{ alignSelf: 'flex-start' }}
            >
              发送
            </Button>
          </Space.Compact>
          {!currentUser && (
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              请先登录后再发送留言
            </Text>
          )}
        </div>

        <Spin spinning={loading}>
          {messages.length === 0 ? (
            <Empty description="暂无留言" />
          ) : (
            <>
              <List
                itemLayout="horizontal"
                dataSource={messages}
                renderItem={(msg) => <MessageItem msg={msg} isReceived={activeTab === 'received'} />}
              />
              {total > pageSize && (
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <Pagination
                    current={page}
                    pageSize={pageSize}
                    total={total}
                    onChange={setPage}
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default Message;
